import type { ExperienceType } from "@intelligems/ig-types/dtos/experiences";
import type { BaseIntegrationDto } from "@intelligems/ig-types/dtos/integrations";
import type { TabType } from "@intelligems/ig-types/fe-only/experiences";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { initAudiences } from "@src/store/slices/audiences";
import { initComponents } from "@src/store/slices/experienceComponents";
import { setCreationModPreset } from "@src/store/slices/experienceInfoState";
import { initKeyMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { resetExperienceProducts } from "@src/store/slices/experienceProducts";
import { initExperimentFromDto } from "@src/store/slices/experiences";
import { initOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { initVariations } from "@src/store/slices/variations";
import type { ExperienceModPreset } from "@src/store/types/experienceInfoState";

export interface NewExperiment {
  testFlowType: ExperienceType;
  integrations: BaseIntegrationDto[] | undefined;
  /** The store's currency, which a price or post-purchase test starts in. */
  storeCurrency: string | undefined;
  shouldShowProfit: boolean | undefined;
  name: string;
  description?: string;
  tab?: TabType;
  preset: ExperienceModPreset | null;
}

/** A blank, unsaved test of one type: what the new test page opens on. */
export const initNewExperiment = createAsyncThunk<void, NewExperiment>(
  "experiences/initNewExperiment",
  (draft, { dispatch }) => {
    const inStoreCurrency =
      draft.testFlowType === "pricing" || draft.testFlowType === "postPurchase";
    // Read back per test group, long after this arrival.
    dispatch(setCreationModPreset(draft.preset));
    dispatch(
      initExperimentFromDto({
        experience: undefined,
        integrations: draft.integrations,
        testFlowType: draft.testFlowType,
        defaultCurrency:
          draft.testFlowType === "pricing"
            ? draft.storeCurrency || "USD"
            : undefined,
        name: draft.name,
        description: draft.description,
        tab: draft.tab,
      })
    );
    dispatch(initVariations({ variations: undefined }));
    dispatch(resetExperienceProducts());
    dispatch(
      initAudiences({
        audience: undefined,
        priceTestDefaultCurrency: inStoreCurrency
          ? draft.storeCurrency || "USD"
          : undefined,
      })
    );
    dispatch(initOnsiteEdits([]));
    dispatch(initComponents([]));
    dispatch(
      initKeyMetrics({
        keyMetrics: [],
        shouldShowProfit: draft.shouldShowProfit,
        hasTestPostPurchase: draft.testFlowType === "postPurchase",
      })
    );
  }
);
